import { supabase } from './supabase'
import type { User, Session } from '@supabase/supabase-js'

export interface AuthHelpers {
  getSession: () => Promise<Session | null>
  onAuthStateChange: (callback: (session: Session | null) => void) => () => void
  signInWithPassword: (email: string, password: string) => Promise<{ user: User | null; error: Error | null }>
  signInWithOAuth: (options: {
    provider: 'google'
    redirectTo: string
  }) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string) => Promise<{ user: User | null; error: Error | null }>
  signOut: () => Promise<void>
  getCurrentUser: () => Promise<User | null>
}

export const auth: AuthHelpers = {
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) {
      console.error('Error getting session:', error)
      return null
    }
    return session
  },

  onAuthStateChange(callback: (session: Session | null) => void) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      callback(session)
    })
    return () => {
      subscription.unsubscribe()
    }
  },

  async signInWithPassword(email: string, password: string) {
    const normalized = email.trim().toLowerCase()
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalized,
      password,
    })

    if (error) {
      if (import.meta.env.DEV) {
        console.error('[auth] signInWithPassword', {
          code: 'code' in error ? error.code : undefined,
          message: error.message,
        })
      }
      return { user: null, error }
    }

    return { user: data.user, error: null }
  },

  async signInWithOAuth({ provider, redirectTo }: { provider: 'google'; redirectTo: string }) {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
      },
    })
    return { error: error as Error | null }
  },

  async signUp(email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })

    if (error) {
      return { user: null, error }
    }

    return { user: data.user, error: null }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) {
      console.error('Error signing out:', error)
      throw error
    }
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) {
      console.error('Error getting user:', error)
      return null
    }
    return user
  },
}
